import { driver } from 'driver.js'
import type { Driver } from 'driver.js'
import 'driver.js/dist/driver.css'

export interface TourStep {
  element: string
  popover: {
    title: string
    description: string
    side?: 'top' | 'bottom' | 'left' | 'right'
    align?: 'start' | 'center' | 'end'
  }
}

export const getWizardStep1Config = (isDarkMode: boolean): TourStep[] => [
  {
    element: '[data-tour="contest-search"]',
    popover: {
      title: 'Busque seu concurso',
      description:
        'Para qual prova você quer estudar? Pesquise pelos concursos e provas disponíveis.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="contest-grid"]',
    popover: {
      title: 'Selecione um concurso',
      description: 'Selecione um concurso para continuar.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-tour="wizard-next"]',
    popover: {
      title: 'Pronto para avançar',
      description: 'Agora é só avançar para a próxima etapa.',
      side: 'top',
      align: 'center',
    },
  },
]

export const getWizardStep2Config = (isDarkMode: boolean): TourStep[] => [
  {
    element: '[data-tour="disciplines-list"]',
    popover: {
      title: 'Escolha as disciplinas',
      description: 'Escolha as disciplinas que você quer no ciclo.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-tour="disciplines-level"]',
    popover: {
      title: 'Ajuste relevância e conhecimento',
      description:
        'A RELEVÂNCIA é o grau de importância da disciplina no edital (1-5) e o CONHECIMENTO é o SEU nível de conhecimento na disciplina (0-5).',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-tour="disciplines-weight"]',
    popover: {
      title: 'Cálculo do peso',
      description:
        'O PESO é a porcentagem de horas de estudo que a disciplina irá ocupar no seu plano. O cálculo é feito com base na RELEVÂNCIA e CONHECIMENTO.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-tour="wizard-next"]',
    popover: {
      title: 'Avançar',
      description:
        'Selecione pelo menos uma disciplina para habilitar este botão. Depois avance quando estiver ok.',
      side: 'top',
      align: 'center',
    },
  },
]

export const getWizardStep3Config = (isDarkMode: boolean): TourStep[] => [
  {
    element: '[data-tour="weekly-hours"]',
    popover: {
      title: 'Horas semanais',
      description:
        'Defina quantas horas por semana você pretende estudar. O ideal é definir um valor realista para você.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="session-minmax"]',
    popover: {
      title: 'Duração das sessões',
      description:
        'O tamanho das sessões é a duração de cada sessão de estudo. O ideal é definir um valor que seja confortável para você e que você consiga manter durante o planejamento.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="wizard-next"]',
    popover: {
      title: 'Próximo passo',
      description: 'Avança quando estiver ok.',
      side: 'top',
      align: 'center',
    },
  },
]

export const getWizardStep4Config = (isDarkMode: boolean): TourStep[] => [
  {
    element: '[data-tour="plan-name"]',
    popover: {
      title: 'Nome do planejamento',
      description: 'Escolha um nome que facilite a identificação do seu plano de estudos.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="summary-contest"]',
    popover: {
      title: 'Concurso de interesse',
      description:
        'Revise o concurso selecionado. Você pode clicar em "Editar" para alterar o concurso se necessário.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="summary-organization"]',
    popover: {
      title: 'Organização de estudos',
      description:
        'Revise a carga horária semanal e a duração das sessões de estudo. Você pode editar essas configurações se necessário.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="summary-disciplines"]',
    popover: {
      title: 'Disciplinas selecionadas',
      description:
        'Revise todas as disciplinas que foram selecionadas para o seu plano de estudos. Cada disciplina mostra o peso calculado com base na relevância e conhecimento.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="summary-distribution"]',
    popover: {
      title: 'Distribuição por disciplina',
      description:
        'Visualize como as horas de estudo estão distribuídas entre as diferentes disciplinas. O gráfico mostra a proporção de tempo que será dedicada a cada disciplina.',
      side: 'left',
      align: 'center',
    },
  },
  {
    element: '[data-tour="finish-button"]',
    popover: {
      title: 'Criar planejamento',
      description: 'Clique aqui para criar o planejamento e começar a estudar.',
      side: 'top',
      align: 'center',
    },
  },
]

export interface DashboardTourOptions {
  hasCurrentSession?: boolean
  timerStatus?: 'idle' | 'running' | 'paused' | 'stopped'
}

export const getDashboardTourConfig = (
  isDarkMode: boolean,
  options?: DashboardTourOptions
): TourStep[] => {
  const steps: TourStep[] = []
  const { hasCurrentSession = true } = options || {}

  // 1. Seletor de Plano
  steps.push({
    element: '[data-tour="dashboard-plan-selector"]',
    popover: {
      title: 'Selecionar plano',
      description:
        'Aqui você seleciona qual plano de estudos visualizar. Você pode ter múltiplos planos e alternar entre eles.',
      side: 'bottom',
      align: 'start',
    },
  })

  // 2. Ações do Plano - Gerenciar
  steps.push({
    element: '[data-tour="dashboard-plan-actions"]',
    popover: {
      title: 'Gerenciar plano',
      description: 'Crie um novo plano ou edite as configurações do seu plano atual.',
      side: 'bottom',
      align: 'end',
    },
  })

  // 3. Métricas - Ciclo Concluído
  steps.push({
    element: '[data-tour="dashboard-metric-cycles"]',
    popover: {
      title: 'Ciclos concluídos',
      description:
        'Quantos ciclos completos você já finalizou. Um ciclo é concluído quando todas as sessões são finalizadas.',
      side: 'bottom',
      align: 'center',
    },
  })

  // 4. Métricas - Tempo de Estudo
  steps.push({
    element: '[data-tour="dashboard-metric-hours"]',
    popover: {
      title: 'Tempo de estudo',
      description:
        'Tempo total estudado versus o tempo planejado. Acompanhe seu progresso em horas.',
      side: 'bottom',
      align: 'center',
    },
  })

  // 5. Métricas - Sessões Concluídas
  steps.push({
    element: '[data-tour="dashboard-metric-sessions"]',
    popover: {
      title: 'Sessões concluídas',
      description:
        'Quantas sessões você já completou do total planejado. Cada sessão representa um bloco de estudo focado.',
      side: 'bottom',
      align: 'center',
    },
  })

  // 6. Métricas - Progresso
  steps.push({
    element: '[data-tour="dashboard-metric-progress"]',
    popover: {
      title: 'Progresso geral',
      description:
        'Porcentagem de conclusão do seu plano de estudos, baseado no tempo estudado e sessões concluídas.',
      side: 'bottom',
      align: 'center',
    },
  })

  // 7. Aba "Sessão Atual"
  steps.push({
    element: '[data-tour="dashboard-tab-current"]',
    popover: {
      title: 'Sessão atual',
      description:
        'Esta aba mostra a sessão de estudo em foco. Aqui você encontra a disciplina atual e as aulas disponíveis.',
      side: 'bottom',
      align: 'start',
    },
  })

  // 8. Sessão Atual (card com disciplina)
  if (hasCurrentSession) {
    steps.push({
      element: '[data-tour="dashboard-current-session"]',
      popover: {
        title: 'Sua sessão de estudo',
        description:
          'Esta é a sua sessão de estudo atual. Aqui você vê qual disciplina está em foco, a duração planejada e pode iniciar o cronômetro.',
        side: 'bottom',
        align: 'start',
      },
    })
  }

  // 9. Botão Iniciar Timer (com menção breve sobre ações futuras)
  if (hasCurrentSession) {
    steps.push({
      element: '[data-tour="dashboard-start-timer"]',
      popover: {
        title: 'Iniciar cronômetro',
        description:
          'Clique aqui para iniciar o cronômetro e começar a estudar. Após iniciar, você terá acesso a ações como pausar, parar e alternar para modo fullscreen.',
        side: 'right',
        align: 'center',
      },
    })
  }

  // 10. Status da Sessão (com dropdown)
  if (hasCurrentSession) {
    steps.push({
      element: '[data-tour="dashboard-session-status"]',
      popover: {
        title: 'Status da sessão',
        description:
          'Acompanhe e atualize o status da sua sessão: Pendente (ainda não começou), Em andamento (estudando) ou Concluída (finalizada).',
        side: 'left',
        align: 'center',
      },
    })
  }

  // 11. Filtros de Aulas
  if (hasCurrentSession) {
    steps.push({
      element: '[data-tour="dashboard-filters"]',
      popover: {
        title: 'Filtros de aulas',
        description:
          'Use os filtros para encontrar aulas específicas. Busque por nome ou filtre por progresso (Todas, Em andamento, Concluídas).',
        side: 'top',
        align: 'start',
      },
    })
  }

  // 12. Lista de Aulas/Capítulos
  if (hasCurrentSession) {
    steps.push({
      element: '[data-tour="dashboard-lessons-list"]',
      popover: {
        title: 'Aulas e capítulos',
        description:
          'Lista de todas as aulas e capítulos disponíveis para esta disciplina. Clique em "Começar" para iniciar uma nova aula ou "Continuar" para retomar onde parou.',
        side: 'top',
        align: 'start',
      },
    })
  }

  // 13. Aba "Sequência de Estudo"
  steps.push({
    element: '[data-tour="dashboard-tab-activities"]',
    popover: {
      title: 'Sequência de estudo',
      description:
        'Visualize todas as sessões do ciclo em ordem. Veja quais já foram concluídas (no final da lista) e quais estão pendentes (no início da lista).',
      side: 'bottom',
      align: 'start',
    },
  })

  // 14. Aba "Edital Verticalizado"
  steps.push({
    element: '[data-tour="dashboard-tab-structured"]',
    popover: {
      title: 'Edital verticalizado',
      description:
        'Visualize o conteúdo do edital organizado de forma estruturada, seguindo a organização do curso na plataforma.',
      side: 'bottom',
      align: 'start',
    },
  })

  // 15. Gráfico "Distribuição das Sessões"
  steps.push({
    element: '[data-tour="dashboard-sessions-chart"]',
    popover: {
      title: 'Distribuição das sessões',
      description:
        'Gráfico que mostra como as horas de estudo estão distribuídas entre as diferentes disciplinas do ciclo. Conforme você vai estudando, as disciplinas vão sendo completadas e o gráfico vai atualizando automaticamente.',
      side: 'left',
      align: 'center',
    },
  })

  // 16. Gráfico "Distribuição por Disciplina"
  steps.push({
    element: '[data-tour="dashboard-disciplines-chart"]',
    popover: {
      title: 'Distribuição por disciplina',
      description:
        'Visualize a distribuição geral de horas e sessões por disciplina de acordo com o plano de estudos selecionado.',
      side: 'left',
      align: 'center',
    },
  })

  return steps
}

export const getRestartCycleTourConfig = (isDarkMode: boolean): TourStep[] => {
  return [
    {
      element: '[data-tour="dashboard-restart-cycle"]',
      popover: {
        title: 'Reiniciar ciclo',
        description:
          'Você concluiu todas as disciplinas do ciclo! Agora é hora de reiniciar e começar um novo ciclo com as mesmas configurações. Não se preocupe, todo o progresso será preservado.',
        side: 'top',
        align: 'center',
      },
    },
  ]
}

export const getTimerCardTourConfig = (isDarkMode: boolean): TourStep[] => {
  return [
    {
      element: '[data-tour="timer-card-container"]',
      popover: {
        title: 'Cronômetro de estudo',
        description:
          'Este é o cronômetro da sua sessão de estudo. Aqui você pode acompanhar o tempo restante, pausar, retomar e controlar sua sessão de estudo.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-card-status"]',
      popover: {
        title: 'Status da sessão',
        description: 'O status atual do cronômetro: Em andamento, Pausado, ou Pronto para iniciar.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-card-progress-value"]',
      popover: {
        title: 'Tempo decorrido',
        description:
          'Mostra quanto tempo você já estudou versus o tempo total planejado. O badge mostra tempo extra adicionado, se houver.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-card-play-pause"]',
      popover: {
        title: 'Controle principal',
        description:
          'Use este botão para iniciar, pausar ou retomar sua sessão de estudo. O ícone muda conforme o estado do cronômetro.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-card-stop"]',
      popover: {
        title: 'Parar sessão',
        description:
          'Finalize sua sessão de estudo. Você poderá escolher se deseja salvar o progresso ou descartar.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-card-expand"]',
      popover: {
        title: 'Modo tela cheia',
        description:
          'Expanda o cronômetro para o modo tela cheia para uma experiência mais imersiva e com recursos adicionais.',
        side: 'left',
        align: 'center',
      },
    },
  ]
}

export const getTimerFullScreenTourConfig = (isDarkMode: boolean): TourStep[] => {
  return [
    {
      element: '[data-tour="timer-fullscreen-minimize"]',
      popover: {
        title: 'Minimizar',
        description:
          'Volte para o modo compacto do cronômetro. Você também pode pressionar ESC para minimizar rapidamente.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-close"]',
      popover: {
        title: 'Fechar cronômetro',
        description: 'Feche completamente o cronômetro sem alterar o progresso da sua sessão.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-discipline"]',
      popover: {
        title: 'Disciplina',
        description: 'A disciplina que você está estudando nesta sessão.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-time-display"]',
      popover: {
        title: 'Tempo restante',
        description: 'Tempo restante da sua sessão. Mais fácil de visualizar durante o estudo.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-status"]',
      popover: {
        title: 'Status',
        description: 'O estado atual do cronômetro: Em andamento, Pausado, ou Pronto para iniciar.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-pause-timer"]',
      popover: {
        title: 'Tempo de pausa',
        description:
          'Mostra o tempo acumulado em pausas durante a sessão. Útil para acompanhar quanto tempo você realmente estudou.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-play-pause"]',
      popover: {
        title: 'Controle principal',
        description:
          'Inicie, pause ou retome sua sessão de estudo. O botão muda conforme o estado do cronômetro.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-stop"]',
      popover: {
        title: 'Parar/Finalizar',
        description:
          'Finalize sua sessão de estudo. Você poderá escolher se deseja salvar o progresso ou descartar.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="timer-fullscreen-add-time-buttons"]',
      popover: {
        title: 'Botões de tempo',
        description:
          'Adicione 1, 5, 10 ou 15 minutos extras à sua sessão. Útil quando você quer continuar estudando além do tempo planejado.',
        side: 'top',
        align: 'center',
      },
    },
  ]
}

export const createDriverInstance = (isDarkMode: boolean, onDestroyed?: () => void): Driver => {
  return driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Próximo',
    prevBtnText: 'Anterior',
    doneBtnText: 'Finalizar',
    progressText: '{{current}} de {{total}}',
    allowClose: true,
    overlayColor: isDarkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)',
    overlayOpacity: 0.75,
    stagePadding: 4,
    stageRadius: 8,
    popoverClass: isDarkMode ? 'driver-popover-dark' : 'driver-popover-light',
    popoverOffset: 10,
    smoothScroll: true,
    disableActiveInteraction: true,
    allowKeyboardControl: true,
    onDestroyed: () => {
      // Tour is destroyed - call the callback if provided
      if (onDestroyed) {
        onDestroyed()
      }
    },
  })
}
